import type { Json } from "@/integrations/supabase/types";

/** Built-in keys seeded per org; DB enum `activity_type` mirrors these + `user_defined`. */
export const BUILTIN_ACTIVITY_TYPE_KEYS = [
  "email",
  "call",
  "meeting",
  "task",
  "note",
  "proposal",
  "whatsapp",
] as const;

type BuiltinActivityTypeKey = (typeof BUILTIN_ACTIVITY_TYPE_KEYS)[number];

type ActivityScheduleScope = "always" | "pipelines";

interface AfterCompleteActivitySettings {
  enabled: boolean;
  scope: ActivityScheduleScope;
  pipelineIds: string[];
}

export type FollowUpPreset =
  | "1d"
  | "3d"
  | "1w"
  | "2w"
  | "1m"
  | "3m"
  | "6m"
  | "1y";

interface AfterDealWonSettings {
  enabled: boolean;
  defaultActivityTypeKey: string;
  followUpPreset: FollowUpPreset;
  allowMembersToOptOut: boolean;
}

export interface OrganizationActivitySettings {
  afterCompleteActivity?: AfterCompleteActivitySettings;
  afterDealWon?: AfterDealWonSettings;
}

export const DEFAULT_ACTIVITY_SETTINGS: OrganizationActivitySettings = {
  afterCompleteActivity: {
    enabled: false,
    scope: "always",
    pipelineIds: [],
  },
  afterDealWon: {
    enabled: false,
    defaultActivityTypeKey: "task",
    followUpPreset: "3m",
    allowMembersToOptOut: false,
  },
};

export function parseActivitySettings(json: Json | null | undefined): OrganizationActivitySettings {
  if (!json || typeof json !== "object" || Array.isArray(json)) {
    return { ...DEFAULT_ACTIVITY_SETTINGS };
  }
  const o = json as Record<string, unknown>;
  const merged: OrganizationActivitySettings = {
    afterCompleteActivity: {
      ...DEFAULT_ACTIVITY_SETTINGS.afterCompleteActivity!,
      ...(typeof o.afterCompleteActivity === "object" && o.afterCompleteActivity !== null
        ? (o.afterCompleteActivity as AfterCompleteActivitySettings)
        : {}),
    },
    afterDealWon: {
      ...DEFAULT_ACTIVITY_SETTINGS.afterDealWon!,
      ...(typeof o.afterDealWon === "object" && o.afterDealWon !== null
        ? (o.afterDealWon as AfterDealWonSettings)
        : {}),
    },
  };
  return merged;
}

export function activityUiPrefsDisableAutoSchedule(
  prefs: Json | null | undefined,
  orgId: string | null | undefined
): boolean {
  if (!orgId || !prefs || typeof prefs !== "object" || Array.isArray(prefs)) return false;
  const map = (prefs as { disable_auto_schedule_by_org?: Record<string, boolean> })
    .disable_auto_schedule_by_org;
  if (!map || typeof map !== "object") return false;
  return !!map[orgId];
}
